/** Operators a policy term may use. */
export type DataPolicyOp = 'in' | 'exists';

/** One term of a policy condition. Never names a column — only a label. */
export type DataPolicyTerm =
  | { label: string; op: 'in'; values: string[] }
  | { label: string; op: 'exists' };

/** The stored shape of vDataPolicy.condition. */
export type DataPolicyCondition = {
  version: 1;
  /** Only 'all' (AND across terms) is accepted in v1. 'any' is reserved. */
  match: 'all';
  terms: DataPolicyTerm[];
};

const uniqueFirstSeen = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    result.push(value);
  }
  return result;
};

/** Split on `|`, trim, drop empties, de-duplicate, preserve first-seen order. */
export const parseValues = (input: string): string[] =>
  uniqueFirstSeen(
    input
      .split('|')
      .map((part) => part.trim())
      .filter((part) => part.length > 0),
  );

const renderTerm = (term: DataPolicyTerm): string => {
  if (term.op === 'exists') {
    return `${term.label} IS SET`;
  }
  return `${term.label} IN [${term.values.map((value) => value.toUpperCase()).join(', ')}]`;
};

/** Human-readable, term order preserved. Values upper-cased for display only. */
export const renderCondition = (condition: DataPolicyCondition): string =>
  condition.terms.map(renderTerm).join(' AND ');

const canonicaliseTerm = (term: DataPolicyTerm): DataPolicyTerm => {
  if (term.op === 'exists') {
    return { label: term.label, op: 'exists' };
  }
  return {
    label: term.label,
    op: 'in',
    values: uniqueFirstSeen(
      [...term.values].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    ),
  };
};

/** Sort terms by label, sort/de-dupe values. Returns a new object. */
export const canonicaliseCondition = (
  condition: DataPolicyCondition,
): DataPolicyCondition => ({
  version: condition.version,
  match: condition.match,
  terms: [...condition.terms]
    .map(canonicaliseTerm)
    .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)),
});

export type DataPolicyConditionInput = {
  version: unknown;
  match: unknown;
  terms: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const assertTermShape = (
  term: unknown,
): { label: string; op: string; values?: unknown } => {
  if (
    !isRecord(term) ||
    typeof term.label !== 'string' ||
    typeof term.op !== 'string'
  ) {
    throw new Error('Data policy condition term is malformed');
  }
  return { label: term.label, op: term.op, values: term.values };
};

const assertTermOperator = (term: {
  label: string;
  op: string;
  values?: unknown;
}): void => {
  if (term.op === 'in') {
    if (!Array.isArray(term.values) || term.values.length === 0) {
      throw new Error(
        `Data policy condition term "${term.label}" with op "in" requires values`,
      );
    }
    if (!term.values.every((value) => typeof value === 'string' && value)) {
      throw new Error(
        `Data policy condition term "${term.label}" values must be non-empty strings`,
      );
    }
    return;
  }
  if (term.op === 'exists') {
    if (term.values !== undefined) {
      throw new Error(
        `Data policy condition term "${term.label}" with op "exists" must not have values`,
      );
    }
    return;
  }
  throw new Error(`Unsupported data policy condition op: ${term.op}`);
};

const assertUniqueTerm = (
  term: { label: string },
  seenLabels: Set<string>,
): void => {
  if (seenLabels.has(term.label)) {
    throw new Error(
      `Data policy condition has more than one term for label "${term.label}"`,
    );
  }
  seenLabels.add(term.label);
};

/**
 * Structural validation only (no enrolment check), e.g. for conditions read
 * from a configuration file.
 */
export function validateConditionShape(
  condition: DataPolicyConditionInput,
): asserts condition is DataPolicyCondition {
  if (condition.version !== 1) {
    throw new Error(
      `Unsupported data policy condition version: ${String(condition.version)}`,
    );
  }
  if (condition.match !== 'all') {
    throw new Error(
      `Unsupported data policy condition match: ${String(condition.match)}`,
    );
  }
  if (!Array.isArray(condition.terms) || condition.terms.length === 0) {
    throw new Error('Data policy condition terms must not be empty');
  }

  const seenLabels = new Set<string>();
  for (const term of condition.terms) {
    const shaped = assertTermShape(term);
    assertUniqueTerm(shaped, seenLabels);
    assertTermOperator(shaped);
  }
}

/**
 * Throw a descriptive error on the first invalid field.
 * Unrestricted access is "no data policy", never an empty `terms` array.
 */
export function validateCondition(
  condition: DataPolicyConditionInput,
  enrolledLabelNames: Set<string>,
): asserts condition is DataPolicyCondition {
  validateConditionShape(condition);
  for (const term of condition.terms) {
    if (!enrolledLabelNames.has(term.label)) {
      throw new Error(
        `Data policy condition references label "${term.label}" that is not enrolled`,
      );
    }
  }
}
